import AlertModal from "@/components/modals/alert-modal";
import NotesSheet from "@/components/sheets/notes-sheet/notes-sheet";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useDeleteNoteMutation } from "@/features/note/note.mutations";
import { useNotes } from "@/features/note/note.queries";
import i18n from "@/lib/i18n";
import { toCapitalize } from "@/utils/text-utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { Pencil, Plus, Trash } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";

const notesRouteSchema = z.object({
  mode: z.enum(["create", "update", "view"]).optional(),
  note: z.number().optional(),
});

export const Route = createFileRoute("/_app/(notes)/notes/")({
  validateSearch: zodSearchValidator(notesRouteSchema),
  component: NotesPage,
});

function NotesPage() {
  return (
    <div className="flex h-full flex-col gap-y-2 px-4 py-2">
      <NotesHeader />
      <NotesPageContent />
      <NotesSheet />
    </div>
  );
}

function NotesHeader() {
  const navigate = useNavigate();
  const { t } = useTranslation(["notes"]);

  const createNote = () => {
    navigate({ to: ".", search: { mode: "create" } });
  };

  return (
    <div className="flex flex-row justify-between py-4">
      <div className="flex flex-col">
        <span className="text-xl font-bold">
          {t("page_title", { format: "uppercase" })}
        </span>
        <span className="text-base font-light text-neutral-500">
          {t("page_sub_title")}
        </span>
      </div>
      <div className="flex flex-row space-x-4">
        <Button
          className="flex items-center gap-3"
          onClick={createNote}
          size="default"
        >
          <Plus strokeWidth={4} className="h-6 w-6" />
          {toCapitalize(t("notes", { ns: "common" }))}
        </Button>
      </div>
    </div>
  );
}

function NotesPageContent() {
  const navigate = useNavigate();
  const selectedDeleteId = useRef<number>(0);
  const { data = [], isLoading } = useNotes();
  const { mutateAsync } = useDeleteNoteMutation();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const updateNote = (id: number) => {
    navigate({ to: ".", search: { mode: "update", note: id } });
  };

  const deleteNote = (id: number) => {
    selectedDeleteId.current = id;
    setIsDeleteModalOpen(true);
  };

  const onSuccessDelete = useCallback(async () => {
    await mutateAsync(selectedDeleteId.current);
  }, [mutateAsync]);

  if (isLoading) {
    return <div>Loading....</div>;
  }

  return (
    <>
      <div className="grid grid-cols-3 gap-2">
        {data.map((note) => (
          <Card key={note.id}>
            <CardTitle className="text-center p-2">
              {note.title}
              <Button
                size={"icon"}
                variant={"ghost"}
                onClick={() => updateNote(note.id)}
              >
                <Pencil />
              </Button>
              <Button
                size={"icon"}
                variant={"ghost"}
                onClick={() => deleteNote(note.id)}
              >
                <Trash color="red" />
              </Button>
            </CardTitle>
            <CardDescription className="p-2">
              <Trans
                i18nKey="common:created_by"
                values={{ name: note.createdBy }}
                components={{ strong: <strong /> }}
              />
            </CardDescription>
            <Separator />
            <CardContent
              className="h-40 overflow-hidden text-ellipsis"
              dangerouslySetInnerHTML={{ __html: note.content }}
            />
          </Card>
        ))}
      </div>
      <AlertModal
        title={i18n.t("modal:delete_lesson_title")}
        description={i18n.t("modal:delete_lesson_text")}
        setOpen={setIsDeleteModalOpen}
        isOpen={isDeleteModalOpen}
        onSuccess={onSuccessDelete}
      />
    </>
  );
}
